import {graphql, useStaticQuery} from 'gatsby';
import React, {Fragment, memo} from 'react';
import {FormattedMessage} from 'react-intl';

import Block from '../components/block';
import Columns, {Column} from '../components/columns';
import IntlStringHtml from '../components/intl-string-html';
import RatioImage from '../components/ratio-image';
import SimpleTeaser from '../components/simple-teaser';

import {unreachable} from '../utils';

import HomepageIntro from './page-element/homepage-intro';
import ImageGallery from './page-element/image-gallery';
import Intro from './page-element/intro';
import LinkList from './page-element/link-list';
import TeaserDark from './page-element/teaser-dark';
import TeaserLight from './page-element/teaser-light';
import Youtube from './page-element/youtube';

/**
 * @param {{
 *   text: import('../../tmp/graphql-types').IntlStringHtmlFragment,
 * }} entry
 * @returns {React.ReactElement} ...
 */
function renderText({text}) {
	return (
		<Block padding={true}>
			<IntlStringHtml {...text} />
		</Block>
	);
}

/**
 * @param {{
 *   text: import('../../tmp/graphql-types').IntlStringHtmlFragment,
 *   artistsCount: number,
 * }} entry
 * @returns {React.ReactElement} ...
 */
function renderStats({text, artistsCount}) {
	return (
		<Block padding={true}>
			<SimpleTeaser
				headline={(
					<Fragment>
						<FormattedMessage
							id="home.nArtworks"
							values={{n: artistsCount}}
						/>
						<br />
						<FormattedMessage id="home.nMonths" values={{n: 4}} /><br />
						<FormattedMessage id="home.nTowns" values={{n: 1}} />
					</Fragment>
				)}
			>
				{text && (
					<IntlStringHtml {...text} />
				)}
			</SimpleTeaser>
		</Block>
	);
}

/**
 * @param {{
 *   images: Array<import('../../tmp/graphql-types').RatioImageFragment>,
 * }} entry
 * @returns {React.ReactElement} ...
 */
function renderImages({images}) {
	return (
		<Block padding={true}>
			<Columns
				columns={images.map((img, i) => new Column({hideOnMobile: i >= 2}, (
					<RatioImage {...img} />
				)))}
			/>
		</Block>
	);
}

export default memo(
	/**
	 * @param {{
	 *   element: import("../../tmp/graphql-types").PageElementFragFragment,
	 *   meta: import("../page-metadata").PageMetadata,
	 *   hashState: number,
	 * }} props
	 * @returns {React.ReactElement} ...
	 */
	function PageElement({element, hashState, meta}) {
		/** @type {import("../../tmp/graphql-types").PageElementQuery} */
		const data = useStaticQuery(graphql `
			fragment PageElementFrag on PageElement {
				__typename
				... on PageElementText {
					textText: text {
						...IntlStringHtml
					}
				}
				... on PageElementStats {
					statsText: text {
						...IntlStringHtml
					}
				}
				... on PageElementTeaserDark {
					teaserDarkUrl: url
					teaserDarkHeadline: headline {
						...IntlString
					}
					teaserDarkText: text {
						...IntlStringHtml
					}
					teaserDarkImage: image {
						...RatioImage
					}
				}
				... on PageElementTeaserLight {
					teaserLightUrl: url
					teaserLightHeadline: headline {
						...IntlString
					}
					teaserLightText: text {
						...IntlStringHtml
					}
				}
				... on PageElementImages {
					imagesImages: images {
						...RatioImage
					}
				}
				... on PageElementHomepageIntro {
					homepageIntroEntries: entries {
						...PageElementHomepageIntro
					}
				}
				... on PageElementIntro {
					introImage: image {
						...RatioImage
					}
					introText: text {
						...IntlStringHtml
					}
				}
				... on PageElementLinkList {
					linkListStyle: style
					linkListTitle: title {
						...IntlString
					}
					linkListEntries: entries {
						title {
							...IntlString
						}
						text {
							...IntlString
						}
						url {
							...IntlString
						}
					}
				}
				... on PageElementImageGallery {
					imageGalleryTitle: title {
						...IntlString
					}
					imageGalleryAddDownloadLink: addDownloadLink
					imageGalleryEntries: entries {
						title {
							...IntlString
						}
						local {
							ext
							publicURL
						}
						...ImageGallery
					}
				}

				... on PageElementYoutube {
					title {
						...IntlString
					}
					videoId
				}
			}

			query PageElement {
				allArtistsJson {
					# TODO: filter !fixedProjects?
					totalCount
				}
			}
		`);

		switch (element.__typename) {
			case 'PageElementText':
				return renderText({text: element.textText});

			case 'PageElementStats':
				return renderStats({
					text: element.statsText,
					artistsCount: data.allArtistsJson.totalCount,
				});

			case 'PageElementImages':
				return renderImages({images: element.imagesImages});

			case 'PageElementTeaserDark':
				return (
					<TeaserDark
						headline={element.teaserDarkHeadline}
						text={element.teaserDarkText}
						image={element.teaserDarkImage}
						url={element.teaserDarkUrl}
						hashState={hashState}
					/>
				);

			case 'PageElementTeaserLight':
				return (
					<TeaserLight
						headline={element.teaserLightHeadline}
						text={element.teaserLightText}
						url={element.teaserLightUrl}
					/>
				);

			case 'PageElementHomepageIntro':
				return (
					<HomepageIntro
						hashState={hashState}
						entries={element.homepageIntroEntries}
					/>
				);

			case 'PageElementIntro':
				return (
					<Intro
						image={element.introImage}
						text={element.introText}
						meta={meta}
						hashState={hashState}
					/>
				);

			case 'PageElementLinkList':
				return (
					<LinkList
						style={element.linkListStyle}
						title={element.linkListTitle}
						entries={element.linkListEntries}
					/>
				);

			case 'PageElementImageGallery':
				return (
					<ImageGallery
						title={element.imageGalleryTitle}
						addDownloadLink={element.imageGalleryAddDownloadLink}
						entries={element.imageGalleryEntries}
					/>
				);

			case 'PageElementYoutube':
				return (
					<Youtube
						title={element.title}
						videoId={element.videoId}
					/>
				);

			default:
				unreachable();
		}
	}
);
